/**
 * Liest Auth-Daten zuverlässig aus KV / IndexedDB (nach Rehydrate).
 */

import { deserializeFromKv } from '../infrastructure/storage/JsonSerializer';
import { getElkheirDb } from '../infrastructure/storage/elkheirDb';
import { kvGet, kvSet, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import { normalizeBusinessStorageId } from './sharedCore';

function idsMatch(a, b) {
  if (a == null || b == null) return false;
  return normalizeBusinessStorageId(a) === normalizeBusinessStorageId(b);
}

export function normalizeCredential(value) {
  return String(value ?? '').trim();
}

/** Vergleicht Codes (String + numerische Codes wie "0123" vs 123). */
export function credentialsMatch(stored, entered) {
  const a = normalizeCredential(stored);
  const b = normalizeCredential(entered);
  if (!a || !b) return false;
  if (a === b) return true;
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    return parseInt(a, 10) === parseInt(b, 10);
  }
  return false;
}

/**
 * Verify a secret that may be PBKDF2-hashed or legacy plaintext.
 * @param {string} entered
 * @param {string|null|undefined} stored
 */
export async function verifyStoredSecret(entered, stored) {
  const raw = normalizeCredential(stored);
  const candidate = normalizeCredential(entered);
  if (!raw || !candidate) return false;

  try {
    const { isPbkdf2PasswordHash, verifyPasswordPbkdf2 } = await import('./passwordKdf');
    if (isPbkdf2PasswordHash(raw)) {
      return verifyPasswordPbkdf2(candidate, raw);
    }
  } catch {
    /* fall through to plaintext */
  }

  return credentialsMatch(raw, candidate);
}

export function parseOfflineBusinessList(data) {
  if (Array.isArray(data)) return data;
  if (typeof data === 'string' && data.trim()) {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * offline_users = [{ id, type, business: { password, username, … } }]
 * Passwort liegt oft nur im verschachtelten business-Objekt.
 */
export function extractBusinessesFromOfflineUsers(usersOrRaw) {
  const list = parseOfflineBusinessList(usersOrRaw);
  const businesses = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    if (entry.business && typeof entry.business === 'object') {
      businesses.push({
        ...entry.business,
        id: entry.business.id || entry.id
      });
    } else if (entry.id && (entry.username || entry.email || entry.password || entry.business_name)) {
      businesses.push(entry);
    }
  }
  return businesses;
}

function normalizeLoginIdentifier(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Business für Login suchen — username, email, phone, und offline_users.
 * @param {string} identifier
 * @param {Array} businesses — bereits gemergte Liste
 */
export function findBusinessForLogin(identifier, businesses) {
  const needle = normalizeLoginIdentifier(identifier);
  if (!needle) return null;

  const matchesIdentifier = (b) => {
    if (!b || typeof b !== 'object') return false;
    const fields = [b.username, b.email, b.phone, b.business_name, b.owner_name];
    return fields.some((f) => normalizeLoginIdentifier(f) === needle);
  };

  let match = businesses.find(matchesIdentifier);
  if (match) return match;

  const fromUsers = extractBusinessesFromOfflineUsers([
    ...readAuthListFromNativeLocalStorage('offline_users'),
    ...parseOfflineBusinessList(kvGet('offline_users'))
  ]);
  match = fromUsers.find(matchesIdentifier);
  if (match) {
    const existing = businesses.find((b) => idsMatch(b.id, match.id));
    return mergeOfflineBusinessRecords([existing, match].filter(Boolean))[0] || match;
  }

  if (businesses.length === 1) {
    return businesses[0];
  }

  return null;
}

/**
 * Passwort-Hash aus offline_businesses + offline_users für ein Business.
 */
export function resolveBusinessPasswordHash(business, businessesFromUsers = null) {
  if (!business) return null;
  if (business.password != null && String(business.password).length > 0) {
    return business.password;
  }
  const users =
    businessesFromUsers ||
    extractBusinessesFromOfflineUsers([
      ...readAuthListFromNativeLocalStorage('offline_users'),
      ...parseOfflineBusinessList(kvGet('offline_users'))
    ]);
  const nested = users.find((b) => idsMatch(b.id, business.id));
  return nested?.password || null;
}

function businessRecordScore(business) {
  if (!business || typeof business !== 'object') return 0;
  let score = 0;
  if (business.password != null && String(business.password).length > 0) score += 100;
  if (business.manager_code != null && String(business.manager_code).trim()) score += 20;
  if (business.username) score += 5;
  if (business.email) score += 5;
  if (business.business_name) score += 3;
  return score;
}

/**
 * Führt offline_businesses aus Cache, IDB, LS und offline_users zusammen.
 * Bevorzugt Einträge mit Passwort (häufig in IDB/LS, aber Cache nach Recovery ohne Passwort).
 */
export function mergeOfflineBusinessRecords(...sources) {
  const byId = new Map();

  for (const source of sources) {
    const list = parseOfflineBusinessList(source);
    for (const business of list) {
      if (!business?.id) continue;
      const key = normalizeBusinessStorageId(business.id) || String(business.id);
      const prev = byId.get(key);
      if (!prev) {
        byId.set(key, { ...business });
        continue;
      }
      const merged = {
        ...prev,
        ...business,
        id: business.id || prev.id,
        password: prev.password || business.password,
        manager_code: prev.manager_code || business.manager_code,
        username: prev.username || business.username,
        email: prev.email || business.email
      };
      byId.set(
        key,
        businessRecordScore(business) >= businessRecordScore(prev) ? merged : { ...merged, ...prev, password: merged.password, manager_code: merged.manager_code }
      );
    }
  }

  return [...byId.values()];
}

const AUTH_ENSURE_KEYS = Object.freeze([
  'offline_businesses',
  'offline_users',
  'currentUser'
]);

/** Coalesce / TTL — full rehydrateCacheFromIdb wipes the KV cache and was spamming every auth read. */
let _authEnsurePromise = null;
let _authEnsureDoneAt = 0;
const AUTH_ENSURE_TTL_MS = 60_000;

/**
 * Ensure auth keys are in the tab cache.
 * @param {{ force?: boolean }} [options] — force=true clears+reloads full IDB cache (reinstall/heal only)
 */
export async function ensureAuthStorageRehydrated(options = {}) {
  const force = Boolean(options?.force);
  const now = Date.now();
  if (!force && _authEnsurePromise) {
    return _authEnsurePromise;
  }
  if (!force && _authEnsureDoneAt && now - _authEnsureDoneAt < AUTH_ENSURE_TTL_MS) {
    return undefined;
  }

  const run = (async () => {
    try {
      const { getStorageEngineRef } = await import('../infrastructure/storage/storageEngineRef');
      const backend = getStorageEngineRef()._backend;
      if (!backend) return;

      if (force && typeof backend.rehydrateCacheFromIdb === 'function') {
        // Reinstall / explicit recovery — full wipe+reload once
        await backend.rehydrateCacheFromIdb();
      } else if (typeof backend.openAndMigrate === 'function' && !backend.isReady?.()) {
        await backend.openAndMigrate();
      } else if (typeof backend.ensureKeysLoaded === 'function' && backend.isReady?.()) {
        // Hot path: load only auth keys — never clear the whole cache mid-session
        await backend.ensureKeysLoaded(AUTH_ENSURE_KEYS);
      } else if (typeof backend.rehydrateCacheFromIdb === 'function' && !backend.isReady?.()) {
        await backend.rehydrateCacheFromIdb();
      }
    } catch {
      /* ignore */
    }

    // AppData catalog (%APPDATA%/…/aman-offline-auth.json) — survives origin mismatch
    // when customer packs are opened under electron-dev (http://127.0.0.1) vs file://.
    try {
      const { syncOfflineAuthFromAppData } = await import('../services/offlineAuthAppData');
      const sync = await syncOfflineAuthFromAppData();
      if (sync?.businessCount > 0) {
        const list = parseOfflineBusinessList(kvGet('offline_businesses'));
        if (list.length) {
          writeAuthListToNativeLocalStorage('offline_businesses', list);
          writeAuthListToNativeLocalStorage(
            'offline_users',
            parseOfflineBusinessList(kvGet('offline_users'))
          );
        }
      }
    } catch {
      /* ignore */
    }
  })();

  if (!force) {
    _authEnsurePromise = run.finally(() => {
      _authEnsureDoneAt = Date.now();
      _authEnsurePromise = null;
    });
    return _authEnsurePromise;
  }

  await run;
  _authEnsureDoneAt = Date.now();
  return undefined;
}

/**
 * @param {string} key
 * @returns {Promise<unknown|null>}
 */
export async function readKvDirect(key) {
  if (!key) return null;
  try {
    const cached = kvGet(key);
    if (cached != null) return cached;

    const db = getElkheirDb();
    await db.open();
    const row = await db.kv_items.get(key);
    if (row?.value == null) return null;
    return deserializeFromKv(row.value);
  } catch {
    return null;
  }
}

/**
 * @param {string} prefix
 * @returns {Promise<Array<{ key: string, value: unknown }>>}
 */
export async function readKvEntriesWithPrefix(prefix) {
  await ensureAuthStorageRehydrated();
  const seen = new Map();

  try {
    const db = getElkheirDb();
    await db.open();
    const rows = await db.kv_items.toArray();
    for (const row of rows) {
      if (!row?.key || !String(row.key).startsWith(prefix)) continue;
      seen.set(row.key, deserializeFromKv(row.value));
    }
  } catch {
    /* ignore */
  }

  try {
    const { listKvKeys } = await import('../infrastructure/storage/kvAccess');
    for (const key of listKvKeys()) {
      if (!key.startsWith(prefix) || seen.has(key)) continue;
      const value = kvGet(key);
      if (value != null) seen.set(key, value);
    }
  } catch {
    /* ignore */
  }

  return [...seen.entries()].map(([key, value]) => ({ key, value }));
}

function readOfflineBusinessesSync() {
  return parseOfflineBusinessList(kvGet('offline_businesses'));
}

async function readOfflineBusinessesFromIdbRaw() {
  try {
    const db = getElkheirDb();
    await db.open();
    const row = await db.kv_items.get('offline_businesses');
    if (row?.value == null) return [];
    return parseOfflineBusinessList(deserializeFromKv(row.value));
  } catch {
    return [];
  }
}

async function readAllOfflineUserEntries() {
  const users = [];
  users.push(...parseOfflineBusinessList(kvGet('offline_users')));
  users.push(...readAuthListFromNativeLocalStorage('offline_users'));

  try {
    const db = getElkheirDb();
    await db.open();
    const row = await db.kv_items.get('offline_users');
    if (row?.value != null) {
      users.push(...parseOfflineBusinessList(deserializeFromKv(row.value)));
    }
  } catch {
    /* ignore */
  }

  return users;
}

/** Natives Chromium-localStorage (AppData) — primäre Offline-Auth-Quelle. */
export function readAuthListFromNativeLocalStorage(key) {
  if (typeof window === 'undefined' || !window.localStorage || !key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? parseOfflineBusinessList(raw) : [];
  } catch {
    return [];
  }
}

export function writeAuthListToNativeLocalStorage(key, data) {
  if (typeof window === 'undefined' || !window.localStorage || !key) return false;
  try {
    const raw = typeof data === 'string' ? data : JSON.stringify(data ?? []);
    window.localStorage.setItem(key, raw);
    return true;
  } catch {
    return false;
  }
}

function readOfflineBusinessesFromLocalStorage() {
  return readAuthListFromNativeLocalStorage('offline_businesses');
}

// FIX: Track whether we have already triggered a reinstall-recovery sync this session.
// Prevents repeated AppData reads on every login click when data is simply missing.
let _reinstallRecoverySyncDone = false;
let _reinstallRecoverySyncPromise = null;

/**
 * FIX: Detects reinstallation scenario (empty KV + empty localStorage + AppData may exist).
 * Triggers a synchronous-looking AppData restore so the next login attempt finds credentials.
 * @returns {Promise<void>}
 */
export async function triggerReinstallRecoveryIfNeeded() {
  if (_reinstallRecoverySyncDone) return;
  // Only trigger when both KV and localStorage are empty
  const fromKv = readOfflineBusinessesSync();
  const fromLs = readOfflineBusinessesFromLocalStorage();
  if (fromKv.length > 0 || fromLs.length > 0) {
    _reinstallRecoverySyncDone = true;
    return;
  }
  // Data is missing — try AppData restore
  if (!_reinstallRecoverySyncPromise) {
    _reinstallRecoverySyncPromise = ensureAuthStorageRehydrated({ force: true }).finally(() => {
      _reinstallRecoverySyncDone = true;
    });
  }
  await _reinstallRecoverySyncPromise;
}

/** Sync merge for login — no IDB rehydrate / AppData (those run in background). */
export function readOfflineBusinessesMergedSync() {
  const businessesFromUsers = extractBusinessesFromOfflineUsers([
    ...readAuthListFromNativeLocalStorage('offline_users'),
    ...parseOfflineBusinessList(kvGet('offline_users'))
  ]);
  const user = kvGet('currentUser');
  return mergeOfflineBusinessRecords(
    readOfflineBusinessesFromLocalStorage(),
    businessesFromUsers,
    readOfflineBusinessesSync(),
    user?.business?.id ? [user.business] : []
  );
}

/**
 * Alle Quellen zusammenführen und bei Bedarf repariertes offline_businesses zurückschreiben.
 */
export async function readOfflineBusinessesMerged(options = {}) {
  const { repair = true, rehydrate = true } = options;
  if (rehydrate) {
    await ensureAuthStorageRehydrated();
  }

  const businessesFromUsers = extractBusinessesFromOfflineUsers(await readAllOfflineUserEntries());

  const merged = mergeOfflineBusinessRecords(
    readOfflineBusinessesFromLocalStorage(),
    businessesFromUsers,
    await readOfflineBusinessesFromIdbRaw(),
    readOfflineBusinessesSync(),
    (() => {
      const user = kvGet('currentUser');
      return user?.business?.id ? [user.business] : [];
    })()
  );

  if (repair && merged.length > 0) {
    const current = mergeOfflineBusinessRecords(
      readOfflineBusinessesFromLocalStorage(),
      readOfflineBusinessesSync()
    );
    const needsRepair = merged.some((entry) => {
      const prev = current.find((c) => idsMatch(c.id, entry.id));
      return (
        (entry.password && !prev?.password) ||
        (entry.manager_code && !prev?.manager_code)
      );
    });
    if (needsRepair) {
      writeAuthListToNativeLocalStorage('offline_businesses', merged);
      kvSet('offline_businesses', merged);
      try {
        await kvSetPersisted('offline_businesses', merged);
        console.info('[authCredentialRead] Repaired offline_businesses from localStorage/IDB');
      } catch {
        /* ignore */
      }
    }
  }

  return merged;
}

export async function readOfflineBusinesses() {
  return readOfflineBusinessesMerged({ repair: true });
}

/**
 * @param {string|null} [businessId]
 * @returns {Set<string>}
 */
export function collectManagerCodesForBusiness(businessId = null) {
  const codes = new Set();
  const bid = businessId ? normalizeBusinessStorageId(businessId) : null;

  const add = (value) => {
    const normalized = normalizeCredential(value);
    if (normalized) codes.add(normalized);
  };

  for (const business of mergeOfflineBusinessRecords(
    readOfflineBusinessesFromLocalStorage(),
    extractBusinessesFromOfflineUsers([
      ...readAuthListFromNativeLocalStorage('offline_users'),
      ...parseOfflineBusinessList(kvGet('offline_users'))
    ]),
    readOfflineBusinessesSync()
  )) {
    if (!bid || idsMatch(business?.id, bid)) {
      add(business?.manager_code);
    }
  }

  const currentBusiness = kvGet('currentBusiness');
  if (
    currentBusiness &&
    typeof currentBusiness === 'object' &&
    !Array.isArray(currentBusiness) &&
    currentBusiness.manager_code &&
    (!bid || idsMatch(currentBusiness.id, bid))
  ) {
    add(currentBusiness.manager_code);
  }

  const currentUser = kvGet('currentUser');
  if (
    currentUser &&
    typeof currentUser === 'object' &&
    !Array.isArray(currentUser) &&
    currentUser.business?.manager_code &&
    (!bid || idsMatch(currentUser.business.id, bid))
  ) {
    add(currentUser.business.manager_code);
  }

  if (!bid) {
    add(kvGet('managerCode'));
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        add(window.localStorage.getItem('managerCode'));
      }
    } catch {
      /* ignore */
    }
  } else {
    const saved = kvGet('managerCode');
    const offline = mergeOfflineBusinessRecords(
      readOfflineBusinessesFromLocalStorage(),
      readOfflineBusinessesSync()
    ).find((b) => idsMatch(b.id, bid));
    if (offline?.manager_code) {
      add(offline.manager_code);
    } else {
      add(saved);
    }
  }

  return codes;
}

/**
 * Sync check — plaintext / numeric legacy only.
 * Prefer {@link isManagerCodeValidAsync} when PBKDF2 hashes may be present.
 * @param {string} code
 * @param {string|null} [businessId]
 */
export function isManagerCodeValid(code, businessId = null) {
  const entered = normalizeCredential(code);
  if (!entered) return false;
  for (const candidate of collectManagerCodesForBusiness(businessId)) {
    if (credentialsMatch(candidate, entered)) return true;
  }
  return false;
}

/**
 * Async manager-code check — supports PBKDF2 hashes and legacy plaintext.
 * @param {string} code
 * @param {string|null} [businessId]
 */
export async function isManagerCodeValidAsync(code, businessId = null) {
  const entered = normalizeCredential(code);
  if (!entered) return false;
  for (const candidate of collectManagerCodesForBusiness(businessId)) {
    if (await verifyStoredSecret(entered, candidate)) return true;
  }
  return false;
}

/**
 * @param {string|null} [businessId]
 * @returns {string|null}
 */
export function resolvePreferredManagerCode(businessId = null) {
  const codes = collectManagerCodesForBusiness(businessId);
  if (codes.size === 0) return null;

  const bid = businessId ? normalizeBusinessStorageId(businessId) : null;
  if (bid) {
    const offline = mergeOfflineBusinessRecords(
      readOfflineBusinessesFromLocalStorage(),
      readOfflineBusinessesSync()
    ).find((b) => idsMatch(b.id, bid));
    if (offline?.manager_code) {
      return normalizeCredential(offline.manager_code);
    }
  }

  return [...codes][0] || null;
}
